/**
 * Firebase Auth svarar med engelska felkoder. Här översätts de till något
 * en förälder som köpt ett Lirahäfte faktiskt förstår.
 */

const MESSAGES: Record<string, string> = {
  'auth/invalid-email': 'E-postadressen ser inte riktig ut. Kontrollera stavningen.',
  'auth/user-disabled': 'Kontot är avstängt. Kontakta kansliet så hjälper vi dig.',
  'auth/user-not-found': 'Fel e-post eller lösenord.',
  'auth/wrong-password': 'Fel e-post eller lösenord.',
  'auth/invalid-credential': 'Fel e-post eller lösenord.',
  'auth/email-already-in-use':
    'Det finns redan ett konto med den här e-postadressen. Logga in i stället, eller välj "Glömt lösenord".',
  'auth/weak-password': 'Lösenordet måste vara minst 8 tecken.',
  'auth/too-many-requests':
    'För många försök. Vänta någon minut och prova igen, eller återställ lösenordet.',
  'auth/network-request-failed':
    'Ingen kontakt med internet. Kontrollera uppkopplingen och försök igen.',
  'auth/requires-recent-login':
    'Av säkerhetsskäl behöver du logga in på nytt innan du gör det här.',
  'auth/missing-password': 'Fyll i ditt lösenord.',
  'auth/operation-not-allowed': 'Inloggningssättet är avstängt. Kontakta kansliet.',

  /*
   * Firestore svarar med bara kodnamnet, utan "auth/" framför, och med en
   * engelsk mening. Den slank tidigare rakt ut i gränssnittet – en lagledare
   * fick "Missing or insufficient permissions." mitt i avstämningen, vilket
   * varken säger vad som hände eller vad man ska göra.
   *
   * Anrop till Cloud Functions har koder som börjar med "functions/" och bär
   * redan svensk text från HttpsError. De ligger därför inte här, utan får
   * passera vidare till meddelandet nedan.
   */
  'permission-denied': 'Du har inte behörighet till det här. Kontakta kansliet om det känns fel.',
  unauthenticated: 'Du verkar ha blivit utloggad. Logga in igen och försök på nytt.',
  unavailable: 'Ingen kontakt med servern just nu. Kontrollera uppkopplingen och försök igen.',
  'deadline-exceeded': 'Det tog för lång tid. Försök igen om en stund.',
  'resource-exhausted': 'Servern är hårt belastad just nu. Försök igen om en stund.',
  'failed-precondition': 'Det gick inte att spara just nu. Ladda om sidan och försök igen.',
  aborted: 'Någon annan hann före. Ladda om sidan och försök igen.',
  'not-found': 'Posten finns inte längre. Ladda om sidan.',
  cancelled: 'Åtgärden avbröts. Försök igen.',

  /*
   * "internal" är serverns sätt att säga att något kastade utan att bli
   * fångat. Koden är också hela meddelandet, så den slank rakt ut i
   * gränssnittet: en superadmin fick ordet "internal" i en röd ruta.
   *
   * Texten ber om det som faktiskt hjälper – att felet rapporteras – eftersom
   * det per definition är något vi inte förutsett.
   */
  internal:
    'Något gick fel på servern. Försök igen, och rapportera felet om det händer igen.',
  'functions/internal':
    'Något gick fel på servern. Försök igen, och rapportera felet om det händer igen.',
  'functions/unavailable':
    'Ingen kontakt med servern just nu. Kontrollera uppkopplingen och försök igen.',
  'functions/deadline-exceeded': 'Det tog för lång tid. Försök igen om en stund.',
  unimplemented: 'Funktionen finns inte i den här versionen. Ladda om sidan.',
  'data-loss': 'Något gick fel på servern. Rapportera felet så tittar vi på det.',
}

/**
 * Samma felkod kan betyda olika saker beroende på vad man försökte göra.
 *
 * "För många försök" vid inloggning är någon som gissat fel lösenord, och då
 * hjälper det att vänta en stund eller återställa lösenordet. Vid kontoskapande
 * är det Googles gräns på 100 nya konton i timmen från samma nätverk – och den
 * slår till på ett föräldramöte där alla sitter på samma wifi. Där hjälper det
 * inte att vänta en minut, men det hjälper direkt att byta till mobildata,
 * eftersom telefonen då får en annan adress utåt.
 */
const SIGN_UP_MESSAGES: Record<string, string> = {
  'auth/too-many-requests':
    'Många har skapat konto från det här nätverket den senaste timmen. Stäng av wifi och prova igen med mobildata – då går det direkt.',
}

export function authErrorMessage(error: unknown, context?: 'signUp'): string {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : ''

  if (context === 'signUp' && code && SIGN_UP_MESSAGES[code]) return SIGN_UP_MESSAGES[code]

  /*
   * "internal" med en förklaring är inte samma sak som "internal" utan.
   *
   * Kastar servern utan att fånga är meddelandet bara ordet INTERNAL, och då
   * gäller den allmänna texten. Men servern skickar också internal med
   * avsikt, och då alltid med en svensk förklaring. Den byttes ut mot "Något
   * gick fel på servern" – när säkerhetskopian misslyckades fick superadmin
   * aldrig veta vad Google svarat, och felet gick inte att åtgärda.
   */
  if (code === 'functions/internal' || code === 'internal') {
    const message =
      typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message: unknown }).message)
        : ''
    if (message && message.toUpperCase() !== 'INTERNAL') return message
  }

  if (code && MESSAGES[code]) return MESSAGES[code]

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message: unknown }).message)
    // Cloud Functions skickar redan svenska meddelanden via HttpsError.
    if (message && !message.startsWith('Firebase:')) return message
  }

  return 'Något gick fel. Försök igen om en stund.'
}
